import React, {useRef, useState, useEffect, useContext} from 'react'
import {createPopper} from '@popperjs/core'
import {KTIcon} from '../../../helpers'
import {useIntl} from 'react-intl'
import AppContext from '../../../../app/AppContext'
import {Link, useParams} from 'react-router-dom'
import '../sidebar/index.css'
import CreateNewPod from '../../../../app/modules/create-new-pod-wizard/CreateNewPodModal'
import Tippy from '@tippyjs/react'
import 'tippy.js/dist/tippy.css'
import {PrimaryOutlineButton} from '../../../../app/ui/PrimaryOutlineButton'
import {LightOutlineButton} from '../../../../app/ui/LightOutlineButton'

interface AppContext {
  podsData: any[]
  getPodsData: (showToaster?: boolean) => void
  isLoading: boolean
}

const PodSelectModal = () => {
  const intl = useIntl()
  const {id} = useParams()
  const {podsData, getPodsData, isLoading}: AppContext = useContext(AppContext)
  const [isOpen, setIsOpen] = useState(false)
  const [inputVal, setInputVal] = useState('')
  const buttonRef: any = useRef(null)
  const modalRef: any = useRef(null)
  const [showModal, setShowModal] = useState(false)

  const toggleModal = () => {
    setIsOpen((prev) => !prev)
  }

  // Position modal below icon
  useEffect(() => {
    if (isOpen && buttonRef.current && modalRef.current) {
      createPopper(buttonRef.current, modalRef.current, {
        placement: 'bottom-start',
        modifiers: [{name: 'offset', options: {offset: [0, 8]}}],
      })
    }
  }, [isOpen])

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const filteredPods = podsData?.filter((pod) =>
    pod.pod_brand_name?.toLowerCase().includes(inputVal.toLowerCase())
  )

  return (
    <div className='position-relative d-inline-block'>
      <Tippy content='Select or add a new POD'>
        <div
          ref={buttonRef}
          onClick={toggleModal}
          className='btn btn-icon btn-color-gray-700 btn-active-color-primary btn-outline w-40px h-40px'
        >
          <KTIcon iconName='abstract-28' className='fs-1' />
        </div>
      </Tippy>

      {isOpen && (
        <div ref={modalRef} className='card border shadow rounded-3 navbar-pod-modal'>
          {/* Search Field */}
          <div className='d-flex align-items-start mb-2 gap-3'>
            <div className='position-relative flex-grow-1'>
              <KTIcon
                iconName='magnifier'
                className='fs-3 text-muted position-absolute top-50 translate-middle-y ms-3'
              />
              <input
                type='search'
                id='kt_filter_search'
                className='form-control form-control-solid form-control-sm ps-9'
                style={{paddingTop: '8px', paddingBottom: '8px'}}
                placeholder={intl.formatMessage({id: 'LABEL.SEARCH'})}
                value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                autoComplete='off'
              />
            </div>
            <PrimaryOutlineButton
              // className='d-flex align-items-center btn btn-md btn-light-primary ms-4'
              onClick={() => setShowModal(true)}
              iconName='plus'
            >
              {/* <KTIcon iconName='plus' className='fs-2' /> */}
              <span className='d-lg-none'>Add</span>
              <span className='d-none d-lg-block'>Add POD</span>
            </PrimaryOutlineButton>
            <Tippy content='Click to load the latest data.'>
              <LightOutlineButton
                // className='btn btn-md btn-light-primary fw-normal ms-4'
                onClick={() => getPodsData()}
              >
                <i className='ki-solid ki-arrows-circle fs-3'></i>
              </LightOutlineButton>
            </Tippy>
          </div>

          {/* Result List */}
          {isLoading ? (
            <div className='h-100px m-auto d-flex flex-column justify-content-center align-items-center'>
              <div
                className='spinner-border spinner-primary mr-15'
                style={{color: 'var(--bs-primary)'}}
              ></div>
            </div>
          ) : (
            <div className='d-flex flex-column gap-2'>
              {filteredPods?.length === 0 && (
                <div className='text-muted text-center py-3'>No results found</div>
              )}

              <div className='row row-cols-4 row-cols-lg-5 g-2' style={{margin: 0}}>
                {(filteredPods || [])?.map((each) => {
                  const color = each.pod_pref_color?.substring(1) || 'ccc'
                  const name = encodeURIComponent(each.pod_brand_name)
                  const isActive = id === `${each.pod_code}`

                  return (
                    <div className='col' key={each.pod_code}>
                      <Tippy content={each.pod_brand_name}>
                        <Link
                          to={`/${each.pod_code}/dashboard`}
                          className={`d-flex flex-column align-items-center px-2 py-3 rounded h-100 ${
                            isActive
                              ? 'bg-light-primary text-primary fw-bold position-relative'
                              : 'text-gray-800 hover-bg-light'
                          }`}
                          style={{textDecoration: 'none', maxHeight: 110}}
                          onClick={() => {
                            setTimeout(() => setIsOpen(false), 250)
                          }}
                        >
                          <div
                            className='d-flex justify-content-center align-items-center mb-2'
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: 8,
                              backgroundColor: isActive ? '#e0f0ff' : '#f5f5f5',
                            }}
                          >
                            <img
                              className='w-100 h-auto'
                              style={{borderRadius: '6px'}}
                              src={
                                each.pod_logo?.url
                                  ? each.pod_logo?.url
                                  : `${process.env.REACT_APP_API_BASE_URL}/get/profile?name=${name}&color=${color}&size=128`
                              }
                              alt={each.pod_brand_name}
                            />
                          </div>
                          <p
                            className='mb-0 text-truncate text-center w-100'
                            style={{fontSize: 14}}
                          >
                            {each.pod_brand_name}
                          </p>
                          {isActive && (
                            <Link
                              to={`/${each.pod_code}/configuration/information`}
                              className='position-absolute'
                              style={{top: -6, right: -4}}
                            >
                              <KTIcon iconName='pencil' className='fs-2 text-primary' />
                            </Link>
                          )}
                        </Link>
                      </Tippy>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}
      <CreateNewPod
        show={showModal}
        onClose={() => setShowModal(false)}
        fetchPODsList={getPodsData}
      />
    </div>
  )
}

export {PodSelectModal}
